import React, { useState } from 'react';
import { createPortal } from 'react-dom';

const CostBreakdownTooltip = ({ 
  row, 
  isVisible, 
  onMouseEnter, 
  onMouseLeave, 
  onClick,
  triggerRef 
}) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });

  // Calculate cost breakdown from row data
  const calculateBreakdown = (row) => {
    const manufacturing = row.manufacturing_cost || 0;
    const freight = row.freight_cost || 0;
    const duties = row.duties_cost || 0;
    const handling = row.handling_cost || 0;
    const overhead = row.overhead_allocation || 0;
    const adSpend = row.ad_spend_allocation || 0;
    const fulfillment = row.fulfillment_cost || 0;
    
    const total = manufacturing + freight + duties + handling + overhead + adSpend + fulfillment;
    
    return {
      total,
      components: [
        { label: 'Manufacturing', value: manufacturing, percentage: total > 0 ? (manufacturing / total) * 100 : 0 },
        { label: 'Freight', value: freight, percentage: total > 0 ? (freight / total) * 100 : 0 },
        { label: 'Duties', value: duties, percentage: total > 0 ? (duties / total) * 100 : 0 },
        { label: 'Handling', value: handling, percentage: total > 0 ? (handling / total) * 100 : 0 },
        { label: 'Overhead', value: overhead, percentage: total > 0 ? (overhead / total) * 100 : 0 },
        { label: 'Ad Spend', value: adSpend, percentage: total > 0 ? (adSpend / total) * 100 : 0 },
        { label: 'Fulfillment', value: fulfillment, percentage: total > 0 ? (fulfillment / total) * 100 : 0 }
      ]
    };
  };

  // Update tooltip position based on trigger element
  React.useEffect(() => {
    if (isVisible && triggerRef?.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        x: rect.right + 8,
        y: rect.top
      });
    }
  }, [isVisible, triggerRef]);

  if (!isVisible || !row) return null;

  const breakdown = calculateBreakdown(row);

  return createPortal(
    <div 
      className="fixed z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg p-3 min-w-[280px] max-w-[320px]"
      style={{ 
        left: position.x, 
        top: position.y,
        transform: 'translateY(-50%)'
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
    >
      {/* Header */}
      <div className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
        Total Cost: ${(Number(breakdown.total) || 0).toFixed(2)}
      </div>
      
      {/* Separator */}
      <div className="border-t border-gray-300 dark:border-gray-600 mb-2"></div>
      
      {/* Cost Components */}
      <div className="space-y-1.5">
        {breakdown.components.map((component, index) => (
          <div key={index} className="flex justify-between items-center text-xs">
            <span className="text-gray-700 dark:text-gray-300 min-w-[90px]">
              {component.label}:
            </span>
            <div className="flex items-center space-x-2">
              <span className="font-mono text-gray-900 dark:text-gray-100 min-w-[60px] text-right">
                ${(Number(component.value) || 0).toFixed(2)}
              </span>
              <span className="text-gray-500 dark:text-gray-400 min-w-[35px] text-right">
                ({(Number(component.percentage) || 0).toFixed(0)}%)
              </span>
            </div>
          </div>
        ))}
      </div>
      
      {/* Bottom Separator */}
      <div className="border-t border-gray-300 dark:border-gray-600 mt-2 pt-2">
        <div className="text-xs text-gray-500 dark:text-gray-400 text-center cursor-pointer hover:text-blue-600 dark:hover:text-blue-400">
          Click to open Pricing Drawer →
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CostBreakdownTooltip;